import CloseIcon from '@mui/icons-material/Close';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  Paper,
  Skeleton,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { ArcElement, Chart, Tooltip as ChartTooltip } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import FicFidcConcentrationCurve from 'src/components/FicFidcConcentrationCurve';
import FicFidcWalletHistoryChart, {
  formatWalletHistoryMonthLabel,
  getWalletHistoryForPeriod,
} from 'src/components/FicFidcWalletHistoryChart';
import { FIC_FIDC_CHART_COLORS } from 'src/utils/ficFidcChartColors';

import { CONCENTRATION_RANKS } from './concentration/contract';
import {
  formatConcentrationCompetency,
  formatConcentrationCurrency,
  formatConcentrationPercentage,
  wrapConcentrationTooltipLabel,
} from './concentration/formatters';
import { getFicFidcCockpitConcentrationFeedback } from './concentration/statusMessages';

import type { FicFidcCompositionCardViewModel } from './concentration/contract';
import type { FicFidcCockpitConcentrationRankEntry } from './concentration/mapFicFidcCockpitConcentration';
import type { FicFidcCockpitConcentrationResult } from './concentration/useFicFidcCockpitConcentration';
import type { FicFidcConcentrationCurvePoint } from 'src/components/FicFidcConcentrationCurve';
import type { MetaCarteirasFicFidcsWalletHistoryItem } from 'src/services/meta-carteiras-fic-fidcs';

Chart.register(ArcElement, ChartTooltip);

interface FicFidcCompleteViewModalProps {
  open: boolean;
  onClose: () => void;
  concentration: FicFidcCockpitConcentrationResult;
  walletHistory?: MetaCarteirasFicFidcsWalletHistoryItem[];
  isWalletHistoryLoading?: boolean;
  isWalletHistoryError?: boolean;
}

function PortfolioEquitySection({
  history,
  loading,
  error,
  competencyDate,
}: {
  history: MetaCarteirasFicFidcsWalletHistoryItem[];
  loading: boolean;
  error: boolean;
  competencyDate: string;
}) {
  const orderedHistory = getWalletHistoryForPeriod(
    history,
    '12_months',
    competencyDate,
  );
  const firstCompetence = orderedHistory[0]?.competence;
  const lastCompetence = orderedHistory[orderedHistory.length - 1]?.competence;
  const historyRange =
    firstCompetence && lastCompetence
      ? `${formatWalletHistoryMonthLabel(firstCompetence)} – ${formatWalletHistoryMonthLabel(lastCompetence)}`
      : 'Intervalo indisponível';
  const selectedMonth = competencyDate.slice(0, 7);
  const selectedPoint = history.find(
    ({ competence }) => competence.slice(0, 7) === selectedMonth,
  );
  const state = loading
    ? 'loading'
    : error
      ? 'error'
      : !competencyDate
        ? 'unavailable'
        : history.length === 0
          ? 'empty'
          : !selectedPoint
            ? 'partial'
            : selectedPoint.totalVlMercPosFinal === 0
              ? 'zero-total'
              : 'available';

  return (
    <Card
      component="section"
      aria-label="Patrimônio"
      data-testid="fic-fidc-complete-equity"
      data-state={state}
      variant="outlined"
      sx={{ minWidth: 0 }}>
      <CardContent sx={{ p: { xs: 1.5, sm: 2.5 } }}>
        <Box
          sx={{
            alignItems: { xs: 'flex-start', sm: 'center' },
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            gap: 0.75,
            mb: 1,
          }}>
          <Box>
            <Typography component="h3" fontWeight={700} variant="h6">
              Patrimônio
            </Typography>
            <Typography color="text.secondary" variant="body2">
              Evolução mensal do patrimônio líquido da carteira
            </Typography>
          </Box>
          <Box sx={{ flexShrink: 0, textAlign: { xs: 'left', sm: 'right' } }}>
            <Typography
              color="text.secondary"
              display="block"
              variant="caption">
              {historyRange}
            </Typography>
            <Typography color="text.secondary" variant="caption">
              R$ milhões
            </Typography>
          </Box>
        </Box>
        {state === 'unavailable' ? (
          <Alert role="status" severity="info" variant="outlined">
            A competência selecionada não está disponível.
          </Alert>
        ) : (
          <FicFidcWalletHistoryChart
            history={orderedHistory}
            isLoading={loading}
            isError={error}
            presentation="complete"
          />
        )}
        {state === 'partial' && (
          <Alert
            role="status"
            severity="info"
            variant="outlined"
            sx={{ mt: 2 }}>
            O histórico não contém um ponto para a competência selecionada.
          </Alert>
        )}
        {state === 'zero-total' && (
          <Alert
            role="status"
            severity="info"
            variant="outlined"
            sx={{ mt: 2 }}>
            O patrimônio informado para esta competência é zero.
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}

function getFundRankLabel(rank: number) {
  return `TOP ${rank} ${rank === 1 ? 'FIDC' : 'FIDCS'}`;
}

function FundConcentrationRankCard({
  entry,
}: {
  entry: FicFidcCockpitConcentrationRankEntry;
}) {
  const hasValue =
    entry.isAvailable && entry.value !== null && entry.percentage !== null;
  const label = getFundRankLabel(entry.rank);
  const state = hasValue
    ? 'available'
    : entry.isMissingPosition
      ? 'missing-position'
      : 'unavailable';
  const accessibleLabel =
    hasValue && entry.value !== null && entry.percentage !== null
      ? `${label}: ${formatConcentrationPercentage(entry.percentage)} do patrimônio do FIC, ${formatConcentrationCurrency(entry.value)}`
      : `${label}: N/D, valor indisponível`;

  return (
    <Card
      aria-label={accessibleLabel}
      data-state={state}
      data-testid={`fic-fidc-complete-fund-rank-card-${entry.rank}`}
      variant="outlined"
      sx={(theme) => ({
        borderColor:
          entry.rank === 1 && hasValue ? theme.palette.primary.main : 'divider',
        borderRadius: 2,
        boxShadow: theme.shadows[1],
        height: '100%',
      })}>
      <CardContent
        sx={{
          '&:last-child': { pb: 2 },
          minHeight: 116,
          p: { xs: 1.75, sm: 2 },
        }}>
        <Typography color="text.secondary" variant="caption">
          {label}
        </Typography>
        <Typography
          color={hasValue ? 'text.primary' : 'text.disabled'}
          fontWeight={700}
          sx={{ mt: 1 }}
          variant="h5">
          {hasValue && entry.percentage !== null
            ? formatConcentrationPercentage(entry.percentage)
            : 'N/D'}
        </Typography>
        <Typography
          color={hasValue ? 'text.secondary' : 'text.disabled'}
          sx={{ minHeight: 21 }}
          variant="body2">
          {hasValue && entry.value !== null
            ? `${formatConcentrationCurrency(entry.value)} do patrimônio do FIC`
            : entry.isMissingPosition
              ? 'Sem FIDC nesta posição'
              : 'Valor indisponível'}
        </Typography>
      </CardContent>
    </Card>
  );
}

function FundConcentrationSkeleton() {
  return (
    <Box data-testid="fic-fidc-complete-fund-loading">
      <Grid container alignItems="stretch" spacing={2}>
        {CONCENTRATION_RANKS.map((rank) => (
          <Grid item key={rank} md={3} sm={6} xs={12}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent sx={{ minHeight: 116, p: { xs: 1.75, sm: 2 } }}>
                <Skeleton height={18} width="45%" />
                <Skeleton height={34} sx={{ mt: 1 }} width="55%" />
                <Skeleton height={21} width="70%" />
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
      <Skeleton height={340} sx={{ mt: 3 }} variant="rounded" />
    </Box>
  );
}

function FundConcentrationSection({
  concentration,
}: {
  concentration: FicFidcCockpitConcentrationResult;
}) {
  const { data, status } = concentration;
  const resultStatus =
    status === 'loading' || status === 'error'
      ? status
      : data?.status ?? 'unavailable';
  const state = resultStatus === 'ready' ? 'available' : resultStatus;
  const feedback = getFicFidcCockpitConcentrationFeedback(
    resultStatus,
    data?.reason ?? null,
  );
  const curvePoints: FicFidcConcentrationCurvePoint[] =
    data?.curve.map((point) => ({
      rank: point.rank,
      accumulatedValue: point.cumulativeVlMercPosFinal,
      accumulatedPercentage: point.cumulativePercentage,
    })) ?? [];
  const totalFunds =
    data?.totalFunds ?? curvePoints[curvePoints.length - 1]?.rank ?? 0;
  const isLoading = resultStatus === 'loading' && !data;
  const canRenderCurve =
    data &&
    (data.status === 'ready' || data.status === 'partial') &&
    curvePoints.length > 0;
  const hasNoCurve =
    data &&
    (data.status === 'ready' || data.status === 'partial') &&
    curvePoints.length === 0;

  return (
    <Card
      component="section"
      aria-label="Concentração por Fundo"
      data-state={state}
      data-testid="fic-fidc-complete-fund"
      variant="outlined"
      sx={{ minWidth: 0, overflow: 'hidden' }}>
      <CardContent sx={{ p: { xs: 1.5, sm: 2.5 } }}>
        <Box
          sx={{
            alignItems: { xs: 'flex-start', sm: 'center' },
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            gap: 0.75,
            mb: 2,
          }}>
          <Box>
            <Typography component="h3" fontWeight={700} variant="h6">
              Concentração por Fundo
            </Typography>
            <Typography color="text.secondary" variant="body2">
              Participação acumulada dos FIDCs sobre o patrimônio líquido do FIC
            </Typography>
          </Box>
          <Typography color="text.secondary" variant="caption">
            {formatConcentrationCompetency(data?.competence) ||
              'Competência indisponível'}
          </Typography>
        </Box>
        {isLoading ? (
          <FundConcentrationSkeleton />
        ) : (
          <>
            {feedback && (
              <Alert
                role={feedback.severity === 'error' ? 'alert' : 'status'}
                severity={feedback.severity}
                variant="outlined"
                sx={{ mb: 2 }}>
                {feedback.message}
              </Alert>
            )}
            {data && data.ranks.length > 0 && (
              <Grid container alignItems="stretch" spacing={2}>
                {data.ranks.map((entry) => (
                  <Grid item key={entry.rank} md={3} sm={6} xs={12}>
                    <FundConcentrationRankCard entry={entry} />
                  </Grid>
                ))}
              </Grid>
            )}
            {canRenderCurve && (
              <Paper
                variant="outlined"
                sx={(currentTheme) => ({
                  borderColor: currentTheme.palette.divider,
                  borderRadius: 2,
                  mt: 3,
                  p: { xs: 1.5, sm: 2.5 },
                })}>
                <Typography component="h4" fontWeight={600} variant="subtitle1">
                  Curva de concentração acumulada
                </Typography>
                <Box sx={{ minWidth: 0 }}>
                  <FicFidcConcentrationCurve
                    points={curvePoints}
                    totalFunds={totalFunds}
                    height={360}
                    marginTop={1}
                    testId="fic-fidc-complete-fund-curve"
                  />
                </Box>
              </Paper>
            )}
            {hasNoCurve && (
              <Alert
                role="status"
                severity="info"
                variant="outlined"
                sx={{ mt: 2 }}>
                A curva não está disponível para a competência selecionada.
              </Alert>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function compositionFeedback(data: FicFidcCompositionCardViewModel) {
  if (data.reason === 'zero-total') {
    return {
      severity: 'info' as const,
      message:
        'O patrimônio líquido do FIC não é positivo nesta competência; a concentração não pode ser calculada.',
    };
  }

  return getFicFidcCockpitConcentrationFeedback(
    data.status === 'available' ? 'ready' : data.status,
    data.reason,
  );
}

function CompositionSection({
  dimension,
  data,
  loading,
  error,
}: {
  dimension: 'asset' | 'manager';
  data: FicFidcCompositionCardViewModel | null;
  loading: boolean;
  error: boolean;
}) {
  const theme = useTheme();
  const title = dimension === 'manager' ? 'Gestor' : 'Ativo-lastro';
  const description =
    dimension === 'manager'
      ? 'Composição percentual da carteira por gestor dos fundos'
      : 'Composição percentual da carteira por classe de ativo-lastro';
  const feedback = data
    ? compositionFeedback(data)
    : error
      ? {
          severity: 'error' as const,
          message: `Não foi possível carregar a composição por ${title.toLocaleLowerCase('pt-BR')}.`,
        }
      : null;
  const items = data?.items ?? [];
  const state = loading
    ? 'loading'
    : error
      ? 'error'
      : data?.status ?? 'unavailable';
  const isPartial = data?.status === 'partial';
  const isRenderable =
    !error &&
    data &&
    (data.status === 'available' || data.status === 'partial') &&
    items.length > 0;

  return (
    <Card
      component="section"
      aria-label={`Concentração por ${title}`}
      data-testid={`fic-fidc-complete-${dimension}`}
      data-state={state}
      variant="outlined"
      sx={{ minWidth: 0, overflow: 'visible' }}>
      <CardContent sx={{ p: { xs: 1.5, sm: 2.5 } }}>
        <Box
          sx={{
            alignItems: { xs: 'flex-start', sm: 'center' },
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            gap: 0.75,
            mb: 2,
          }}>
          <Box>
            <Typography
              id={`fic-fidc-complete-${dimension}-title`}
              component="h3"
              fontWeight={700}
              variant="h6">
              Concentração por {title}
            </Typography>
            <Typography color="text.secondary" variant="body2">
              {description}
            </Typography>
          </Box>
          <Typography color="text.secondary" variant="caption">
            {formatConcentrationCompetency(data?.competence) ||
              'Competência indisponível'}
          </Typography>
        </Box>
        {loading ? (
          <Skeleton
            data-testid={`fic-fidc-complete-${dimension}-loading`}
            height={300}
            variant="rectangular"
          />
        ) : isRenderable ? (
          <>
            {data.status === 'partial' && feedback && (
              <Alert
                role="status"
                severity={feedback.severity}
                variant="outlined"
                sx={{ mb: 2 }}>
                {feedback.message}
              </Alert>
            )}
            <Box
              data-layout="donut-with-legend"
              sx={{
                alignItems: 'center',
                display: 'grid',
                gap: { xs: 2, md: 4 },
                gridTemplateColumns: {
                  xs: 'minmax(0, 1fr)',
                  md: 'minmax(250px, 0.85fr) minmax(0, 1.15fr)',
                },
                minWidth: 0,
              }}>
              <Box
                sx={{
                  height: { xs: 250, sm: 320 },
                  maxWidth: { xs: 280, md: 340 },
                  mx: 'auto',
                  overflow: 'visible',
                  position: 'relative',
                  width: '100%',
                }}>
                <Doughnut
                  aria-label={`Distribuição completa por ${title.toLocaleLowerCase('pt-BR')}`}
                  data={{
                    labels: items.map((item) => item.name),
                    datasets: [
                      {
                        data: items.map((item) => item.vlMercPosFinal),
                        backgroundColor: items.map(
                          (_, index) =>
                            FIC_FIDC_CHART_COLORS[
                              index % FIC_FIDC_CHART_COLORS.length
                            ],
                        ),
                        borderWidth: 1,
                        borderColor: theme.palette.background.paper,
                      },
                    ],
                  }}
                  options={{
                    cutout: '70%',
                    events: [
                      'mousemove',
                      'mouseout',
                      'touchstart',
                      'touchmove',
                    ],
                    interaction: { intersect: true, mode: 'nearest' },
                    layout: { padding: 28 },
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { display: false },
                      tooltip: {
                        enabled: true,
                        caretPadding: 8,
                        callbacks: {
                          title: (contexts) =>
                            wrapConcentrationTooltipLabel(
                              items[contexts[0]?.dataIndex]?.name ?? title,
                              28,
                            ),
                          label: (context) => {
                            const item = items[context.dataIndex];
                            if (!item) return '';

                            return [
                              `Participação: ${formatConcentrationPercentage(item.percentage, 2)}`,
                              `Montante: ${formatConcentrationCurrency(context.parsed, 2)}`,
                            ];
                          },
                        },
                        padding: 8,
                        position: 'nearest',
                      },
                    },
                  }}
                />
                <Box
                  sx={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    inset: 0,
                    justifyContent: 'center',
                    px: 1,
                    pointerEvents: 'none',
                    position: 'absolute',
                  }}>
                  <Typography
                    fontSize={{ xs: 15, sm: 18 }}
                    fontWeight={600}
                    lineHeight={1.05}
                    sx={{ maxWidth: '78%', textAlign: 'center' }}>
                    {isPartial ? 'Parcial' : '100%'}
                  </Typography>
                  <Typography
                    color="text.secondary"
                    fontSize={11}
                    lineHeight={1.1}
                    variant="caption">
                    {isPartial ? 'cobertura' : 'exposição'}
                  </Typography>
                </Box>
              </Box>
              <Box
                component="ul"
                aria-label={`Composição completa por ${title.toLocaleLowerCase('pt-BR')}`}
                sx={{
                  display: 'grid',
                  gap: 1.25,
                  listStyle: 'none',
                  m: 0,
                  minWidth: 0,
                  p: 0,
                }}>
                {items.map((item, index) => (
                  <Box
                    component="li"
                    key={`${item.name}-${index}`}
                    sx={{
                      alignItems: 'center',
                      borderBottom: 1,
                      borderColor: 'divider',
                      display: 'grid',
                      gridTemplateColumns: 'minmax(0, 1fr) auto',
                      gap: { xs: 1, sm: 2 },
                      minWidth: 0,
                      pb: 1,
                    }}>
                    <Box
                      sx={{
                        alignItems: 'center',
                        display: 'flex',
                        gap: 1,
                        minWidth: 0,
                      }}>
                      <Box
                        aria-hidden
                        sx={{
                          bgcolor:
                            FIC_FIDC_CHART_COLORS[
                              index % FIC_FIDC_CHART_COLORS.length
                            ],
                          borderRadius: '50%',
                          flex: '0 0 auto',
                          height: 10,
                          mt: 0.6,
                          width: 10,
                        }}
                      />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          sx={{ overflowWrap: 'anywhere' }}
                          variant="body2">
                          {item.name}
                        </Typography>
                      </Box>
                    </Box>
                    <Typography
                      align="right"
                      sx={{
                        fontVariantNumeric: 'tabular-nums',
                        whiteSpace: 'nowrap',
                      }}
                      variant="body2">
                      {item.percentage.toLocaleString('pt-BR', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                      %
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </>
        ) : (
          <Alert
            role={error || data?.status === 'error' ? 'alert' : 'status'}
            severity={feedback?.severity ?? 'info'}
            variant="outlined">
            {feedback?.message ??
              'A concentração não está disponível para a competência selecionada.'}
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}

export function FicFidcCompleteViewModal({
  open,
  onClose,
  concentration,
  walletHistory = [],
  isWalletHistoryLoading = false,
  isWalletHistoryError = false,
}: FicFidcCompleteViewModalProps) {
  const competency = concentration.competencyDate;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      aria-labelledby="fic-fidc-complete-view-title"
      scroll="paper">
      <Box
        component="header"
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          px: { xs: 2, sm: 3 },
          py: 2,
        }}>
        <Box
          sx={{
            alignItems: { xs: 'flex-start', sm: 'center' },
            display: 'flex',
            justifyContent: 'space-between',
            gap: 2,
            pr: { xs: 0, sm: 0.5 },
          }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              color="primary.main"
              fontWeight={700}
              letterSpacing={1}
              variant="overline">
              DETALHES DA CARTEIRA
            </Typography>
            <Typography
              id="fic-fidc-complete-view-title"
              component="h2"
              fontWeight={700}
              sx={{ lineHeight: 1.2 }}
              variant="h5">
              Gráficos completos da carteira
            </Typography>
            <Typography color="text.secondary" variant="body2" sx={{ mt: 0.5 }}>
              Acompanhe o patrimônio e a composição da carteira.
            </Typography>
          </Box>
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              flexShrink: 0,
              gap: 1,
            }}>
            <Box
              sx={{ display: { xs: 'none', sm: 'block' }, textAlign: 'right' }}>
              <Typography
                color="text.secondary"
                display="block"
                variant="caption">
                Competência
              </Typography>
              <Typography fontWeight={600} variant="body2">
                {formatConcentrationCompetency(competency) || 'Indisponível'}
              </Typography>
            </Box>
            <IconButton aria-label="Fechar" onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
        <Typography
          color="text.secondary"
          display={{ xs: 'block', sm: 'none' }}
          sx={{ mt: 1 }}
          variant="caption">
          Competência:{' '}
          {formatConcentrationCompetency(competency) || 'Indisponível'}
        </Typography>
      </Box>
      <DialogContent
        dividers
        sx={{ minWidth: 0, overflowX: 'hidden', p: { xs: 1.5, sm: 3 } }}>
        <Alert
          severity="info"
          variant="outlined"
          sx={{
            mb: 1,
          }}>
          Os gráficos mostram os dados disponíveis para a competência
          selecionada.
        </Alert>
        <Box
          id="fic-fidc-complete-charts"
          aria-label="Visualizações completas da carteira"
          sx={{
            display: 'grid',
            gap: { xs: 1.5, sm: 2.5 },
            gridTemplateColumns: 'minmax(0, 1fr)',
            minWidth: 0,
          }}>
          <PortfolioEquitySection
            history={walletHistory}
            loading={isWalletHistoryLoading}
            error={isWalletHistoryError}
            competencyDate={concentration.competencyDate}
          />
          <FundConcentrationSection concentration={concentration} />
          <CompositionSection
            dimension="asset"
            data={concentration.data?.composition.asset ?? null}
            loading={concentration.status === 'loading' && !concentration.data}
            error={concentration.status === 'error' && !concentration.data}
          />
          <CompositionSection
            dimension="manager"
            data={concentration.data?.composition.manager ?? null}
            loading={concentration.status === 'loading' && !concentration.data}
            error={concentration.status === 'error' && !concentration.data}
          />
        </Box>
      </DialogContent>
    </Dialog>
  );
}

export default FicFidcCompleteViewModal;
